/**
 * CLI Feature Flag Management
 *
 * Allows superadmins to manage feature flags via the CLI.
 */

import process from "node:process";
import { resolveConfig, getArgValue, hasFlag } from "./auth.js";
import {
  type LifecycleStage,
  toLifecycleLabel,
  apiRequest,
  fetchList,
} from "./cli-helpers.js";
import { runAnnouncement } from "./feature-flag-announcement.js";
import { runDisableGateCommand, runEnableGateCommand } from "./feature-flag-gates.js";
import { formatBakeSuffix, runLifecycle, runOven } from "./feature-flag-lifecycle.js";

type FeatureFlag = {
  id: string;
  name: string;
  description: string | null;
  lifecycleStage: LifecycleStage;
  finalValue: boolean | null;
  bakeMonitoringPlan?: string | null;
  bakeStartedAt?: string | null;
  bakeReviewIntervalDays?: number | null;
  bakeMaxWindowDays?: number | null;
  createdAt: string;
  lastToggledAt: string;
  isStale: boolean;
  rolloutPercentage?: number | null;
  productCardId: string | null;
  productTitle: string | null;
  organizations?: Array<{
    gateId: string;
    orgId: string;
    orgName: string;
    createdAt: string;
    rolloutPercentageOverride?: number | null;
    excludeFromAnalysis?: boolean;
  }>;
  users?: Array<{
    gateId: string;
    userId: string;
    userEmail: string | null;
    createdAt: string;
  }>;
};

type FlagApiResponse = {
  ok: boolean;
  error?: string;
  flag?: FeatureFlag;
};

type ProductCard = {
  id: string;
  title: string;
  stage: string;
  orgId: string;
  orgName: string;
};

function fetchFlags(apiUrl: string, token: string): Promise<FeatureFlag[]> {
  return fetchList<FeatureFlag>(apiUrl, token, "/superadmin/feature-flags", "flags");
}

async function handleList(argv: string[], apiUrl: string, token: string): Promise<void> {
  const jsonOutput = hasFlag(argv, "--json");
  const flags = await fetchFlags(apiUrl, token);

  if (jsonOutput) {
    console.log(JSON.stringify(flags, null, 2));
    return;
  }

  if (flags.length === 0) {
    console.log("No feature flags found.");
    return;
  }

  for (const flag of flags) {
    const orgCount = flag.organizations?.length ?? 0;
    const userCount = flag.users?.length ?? 0;
    const lifecycle = `lifecycle=${toLifecycleLabel(flag.lifecycleStage)}`;
    // baking and internal are gate-driven like active — no final value.
    const finalValue =
      flag.lifecycleStage === "active" ||
      flag.lifecycleStage === "baking" ||
      flag.lifecycleStage === "internal"
        ? ""
        : ` final=${flag.finalValue === true ? "true" : "false"}`;
    const bake = flag.lifecycleStage === "baking" ? formatBakeSuffix(flag) : "";
    const stale = flag.isStale ? " [STALE]" : "";
    const product = flag.productCardId
      ? ` product=${flag.productTitle ?? flag.productCardId}`
      : "";
    const rollout =
      flag.rolloutPercentage === null || flag.rolloutPercentage === undefined
        ? ""
        : ` rollout=${flag.rolloutPercentage}%`;
    console.log(`  ${flag.name}  ${flag.description ?? ""}  (${lifecycle}${finalValue})${bake}${rollout}  [orgs=${orgCount}, users=${userCount}]${product}${stale}`);

    // Surface per-org rollout overrides / analysis exclusions (only the gates
    // that deviate from the flag default, to keep the listing quiet).
    for (const org of flag.organizations ?? []) {
      const parts: string[] = [];
      if (org.rolloutPercentageOverride !== null && org.rolloutPercentageOverride !== undefined) {
        parts.push(`rollout override ${org.rolloutPercentageOverride}%`);
      }
      if (org.excludeFromAnalysis) parts.push("excluded from analysis");
      if (parts.length) {
        console.log(`      ↳ ${org.orgName}: ${parts.join(", ")}`);
      }
    }
  }
}

async function handleCreate(argv: string[], apiUrl: string, token: string): Promise<void> {
  const name = argv[0];
  if (!name || name.startsWith("--")) {
    console.error("Error: Missing flag name.");
    console.error("Usage: canary feature-flag create <name> [--description <text>] [--product <productCardId>]");
    process.exit(1);
  }

  const description = getArgValue(argv, "--description") ?? null;
  const productCardId = getArgValue(argv, "--product") ?? null;
  const result = await apiRequest<FlagApiResponse>(apiUrl, token, "POST", "/superadmin/feature-flags", {
    name,
    description,
    productCardId,
  });

  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }

  console.log(`Created feature flag: ${name}${productCardId ? ` (product ${productCardId})` : ""}`);
}

async function handleProduct(argv: string[], apiUrl: string, token: string): Promise<void> {
  const name = argv[0];
  const productCardId = getArgValue(argv, "--product");
  const clear = hasFlag(argv, "--clear");

  if (!name || name.startsWith("--")) {
    console.error("Error: Missing flag name.");
    console.error("Usage: canary feature-flag product <name> (--product <productCardId> | --clear)");
    process.exit(1);
  }

  if ((productCardId && clear) || (!productCardId && !clear)) {
    console.error("Error: Provide either --product <productCardId> or --clear.");
    process.exit(1);
  }

  const result = await apiRequest<FlagApiResponse & { flag?: { productTitle?: string | null } }>(
    apiUrl, token, "POST",
    `/superadmin/feature-flags/${encodeURIComponent(name)}/product`,
    { productCardId: clear ? null : productCardId }
  );

  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }

  console.log(
    clear
      ? `Cleared product link for ${name}`
      : `Linked ${name} to product ${result.flag?.productTitle ?? productCardId}`
  );
}

async function handleRollout(argv: string[], apiUrl: string, token: string): Promise<void> {
  const name = argv[0];
  const raw = getArgValue(argv, "--percentage");
  const clear = hasFlag(argv, "--clear");

  if (!name || name.startsWith("--")) {
    console.error("Error: Missing flag name.");
    console.error("Usage: canary feature-flag rollout <name> (--percentage <0-100> | --clear)");
    process.exit(1);
  }

  if ((raw !== undefined && clear) || (raw === undefined && !clear)) {
    console.error("Error: Provide either --percentage <0-100> or --clear.");
    process.exit(1);
  }

  let rolloutPercentage: number | null = null;
  if (!clear) {
    rolloutPercentage = Number.parseInt(raw as string, 10);
    if (!Number.isInteger(rolloutPercentage) || rolloutPercentage < 0 || rolloutPercentage > 100) {
      console.error("Error: --percentage must be an integer 0-100.");
      process.exit(1);
    }
  }

  const result = await apiRequest<FlagApiResponse>(
    apiUrl, token, "POST",
    `/superadmin/feature-flags/${encodeURIComponent(name)}/rollout`,
    { rolloutPercentage }
  );

  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }

  console.log(
    clear
      ? `Cleared rollout for ${name} (legacy gate=on behavior)`
      : `Set ${name} rollout to ${rolloutPercentage}% treatment`
  );
}

async function handleProducts(argv: string[], apiUrl: string, token: string): Promise<void> {
  const jsonOutput = hasFlag(argv, "--json");
  const search = getArgValue(argv, "--search");
  const path = search
    ? `/superadmin/product-cards?search=${encodeURIComponent(search)}`
    : "/superadmin/product-cards";
  const products = await fetchList<ProductCard>(apiUrl, token, path, "products");

  if (jsonOutput) {
    console.log(JSON.stringify(products, null, 2));
    return;
  }

  if (products.length === 0) {
    console.log("No product cards found.");
    return;
  }

  for (const product of products) {
    console.log(`  ${product.id}  ${product.title}  (stage=${product.stage}, org=${product.orgName})`);
  }
}

async function handleDelete(argv: string[], apiUrl: string, token: string): Promise<void> {
  const name = argv[0];
  if (!name || name.startsWith("--")) {
    console.error("Error: Missing flag name.");
    console.error("Usage: canary feature-flag delete <name>");
    process.exit(1);
  }

  const result = await apiRequest<FlagApiResponse>(
    apiUrl, token, "DELETE", `/superadmin/feature-flags/${encodeURIComponent(name)}`
  );

  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }

  console.log(`Deleted feature flag: ${name}`);
}

function printFeatureFlagHelp(): void {
  console.log(
    [
      "Usage: canary feature-flag <sub-command> [options]",
      "",
      "Sub-commands:",
      "  list                                          List all feature flags",
      "  create <name> [--description <text>] [--product <productCardId>]",
      "                                                Create a new flag (optionally linked to a product)",
      "  delete <name>                                 Delete a flag and all its gates",
      "  enable <name> (--target-org <orgId> | --user <userId>)",
      "                                                Enable a flag for an org or user",
      "             [--rollout-override <0-100|clear>] [--exclude-from-analysis | --include-in-analysis]",
      "                                                Per-org (--target-org) rollout override + A-B analysis exclusion;",
      "                                                may also update an already-enabled org in place",
      "  disable <name> (--target-org <orgId> | --user <userId>)",
      "                                                Disable a flag for an org or user (kept as 'was enabled' history)",
      "  lifecycle <name> --stage <stage> [--final-value true|false]",
      "                                                Mark lifecycle + final value",
      "             --stage baking --plan @file [--review-interval <days>] [--max-window <days>]",
      "                                                Put a flag in the oven (requires a linked product card)",
      "  product <name> (--product <productCardId> | --clear)",
      "                                                Link/unlink a flag to a product board item",
      "  rollout <name> (--percentage <0-100> | --clear)",
      "                                                Set the A-B / rollout treatment % (per-run coin flip; clear = legacy gate=on)",
      "  products [--search <text>]                    List product board items (find product card ids)",
      "  oven [--json]                                 List flags currently baking (age + burning state)",
      "  announcement <sub> ...                        Manage a flag's announcement message (see `announcement help`)",
      "",
      "Stages: active, baking, internal, deprecated, ready_for_cleanup",
      "  internal: long-lived internal-only product (gated to internal orgs only; drives the Internal board column)",
      "",
      "Options:",
      "  --target-org <orgId>       Target organization (cross-org superadmin action)",
      "  --user <userId>            Target user",
      "  --product <productCardId>  Product board item (cards.id) to link",
      "  --percentage <0-100>       Treatment-assignment % (rollout sub-command); ramp up to roll out gradually",
      "  --clear                    Clear the product link / rollout % (product or rollout sub-command)",
      "  --search <text>            Filter product cards by title (products sub-command only)",
      "  --final-value <true|false>  Final value for deprecated/ready_for_cleanup",
      "  --clear-final-value         Clear final value (only valid with --stage active)",
      "  --plan @file|\"text\"       Monitoring/telemetry plan (required to enter baking)",
      "  --review-interval <days>    Days between oven-check reviews (baking; default 7)",
      "  --max-window <days>         Days before a baking flag 'burns' (default 30)",
      "  --env <env>                 Target environment (prod, dev, local)",
      "  --json                      Output as JSON (list only)",
      "  --api-url <url>             API URL override (takes precedence over --env)",
      "  --token <key>               API token override",
    ].join("\n")
  );
}

export async function runFeatureFlag(argv: string[]): Promise<void> {
  const [subCommand, ...rest] = argv;

  if (!subCommand || subCommand === "help" || hasFlag(argv, "--help", "-h")) {
    printFeatureFlagHelp();
    return;
  }

  const { apiUrl, token } = await resolveConfig(argv);

  switch (subCommand) {
    case "list":
      await handleList(rest, apiUrl, token);
      break;
    case "create":
      await handleCreate(rest, apiUrl, token);
      break;
    case "delete":
      await handleDelete(rest, apiUrl, token);
      break;
    case "enable":
      await runEnableGateCommand(rest, apiUrl, token);
      break;
    case "disable":
      await runDisableGateCommand(rest, apiUrl, token);
      break;
    case "lifecycle":
      await runLifecycle(rest, apiUrl, token);
      break;
    case "product":
      await handleProduct(rest, apiUrl, token);
      break;
    case "rollout":
      await handleRollout(rest, apiUrl, token);
      break;
    case "products":
      await handleProducts(rest, apiUrl, token);
      break;
    case "oven":
      await runOven(rest, apiUrl, token);
      break;
    case "announcement":
      await runAnnouncement(rest, apiUrl, token);
      break;
    default:
      console.error(`Unknown sub-command: ${subCommand}`);
      printFeatureFlagHelp();
      process.exit(1);
  }
}
